import React, { useState } from 'react';
import { Account, AccStatus } from 'entities/account/model/types';
import { useGetAccountsQuery, useLazyGetAccountUserMetaQuery } from 'entities/account/api';
import { Card } from 'entities/card/model/types';
import { useDispatch, useSelector } from 'react-redux';
import { getEMIAccountMeta, setCurrentAccount } from 'shared/config/store/reducers/accountsSlice';
import { EMI } from 'shared/model/types';
import { setCurrentCard } from 'shared/config/store/reducers/cardsSlice';
import { setCurrentEmi } from 'shared/config/store/reducers/appSlice';
import { VerificationStatus } from 'entities/user/model/types';
import { RootState } from 'shared/config/store/store';
import { Divider, Modal, Space } from 'antd';
import { useStartVerificationMutation } from '../api';
import { useNavigate } from 'react-router';
import PlanContainer from 'shared/ui/PlanContainer/PlanContainer';
import { useIntl } from 'react-intl';
import { setUserSecure } from 'shared/config/store/reducers/userSlice';
import VerificationStatusModal from './VerificationStatusModal';
import { OnfidoVerification } from 'features/account/add-account/ui/OnfidoVerification';
import AccountItem from './AccountItem/AccountItem';

export type PlanProps = {
  emi: EMI;
  planName: string;
  accounts: Record<number, Account>;
  status: AccStatus;
  cards: Record<number, Card[]>;
};

const Plan: React.FC<PlanProps> = ({ emi, accounts, status, cards, planName }) => {
  const secured = useSelector((state: RootState) => state.user.secured);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalOptions, setStatusModalOptions] = useState<{
    status: VerificationStatus;
    onAction?: () => void;
    actionText?: string;
  }>({
    status: VerificationStatus.Init
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { formatMessage: t } = useIntl();
  const emiMeta = useSelector((state: RootState) => getEMIAccountMeta(state, emi));
  const accountsArray = Object.values(accounts);
  const hasAccounts = accountsArray.length > 0;
  const totalBalance = hasAccounts 
    ? accountsArray.reduce((sum, current) => sum + current.available, 0)
    : 0;

  const [getAccountUserMeta, { isLoading: isLoadingEmiMeta }] = useLazyGetAccountUserMetaQuery();
  const { isLoading } = useGetAccountsQuery(
    { emi }
  );
  const [startVerificationApi, { isLoading: isVerifying }] = useStartVerificationMutation();

  const toggleSecured = () => {
    dispatch(setUserSecure(!secured));
  };

  const closeVerificationModal = () => setShowVerificationModal(false);

  const onAccountClick = async (accountId: number) => {
    console.log('Current verification status:', emiMeta.kyc_status);

    let kycStatus = emiMeta.kyc_status;

    if (kycStatus === VerificationStatus.Rejected) {
      setStatusModalOpen(true);
      setStatusModalOptions({
        status: VerificationStatus.Rejected,
        actionText: t({ id: 'verification.retry.buttonLabel' }),
        onAction: async () => {
          try {
            await startVerificationApi(emi).unwrap();
            setStatusModalOptions({
              status: VerificationStatus.Pending
            });
          } catch (error) {
            console.log('Failed to start verification API', error);
          }
        }
      });
      return;
    }

    if (kycStatus !== VerificationStatus.Verified) {
      try {
        const { response } = await getAccountUserMeta({ emi }).unwrap();
        kycStatus = response.kyc_status;

        console.log('Updated verification status (kyc_status):', kycStatus);

        if (kycStatus === VerificationStatus.Rejected) {
          setStatusModalOpen(true);
          setStatusModalOptions({
            status: VerificationStatus.Rejected,
            actionText: 'Ok',
            onAction: () => setStatusModalOpen(false)
          });
          return;
        }

        if ([VerificationStatus.Pending].includes(kycStatus)) {
          setStatusModalOpen(true);
          setStatusModalOptions({
            status: VerificationStatus.Pending,
            actionText: 'Ok',
            onAction: () => setStatusModalOpen(false)
          });
          return;
        }

        if (kycStatus === VerificationStatus.Init) {
          console.log('Starting verification...');
          setShowVerificationModal(true);
          console.log('Starting startVerificationApi...');

          setStatusModalOptions({
            status: VerificationStatus.Pending
          });

          return;
        }

        if (kycStatus === VerificationStatus.Verified) {
          setStatusModalOpen(true);
          setStatusModalOptions({
            status: VerificationStatus.Verified,
            actionText: 'Ok',
            onAction: () => setStatusModalOpen(false)
          });
        } else {
          return;
        }
      } catch (error) {
        console.error('Error fetching verification status:', error);
        return;
      }
    }

    console.log('Verification confirmed, accessing the account.');

    dispatch(setCurrentEmi(emi));
    dispatch(setCurrentAccount({ emi, accountId }));

    const card = cards[accountId]?.[0];
    if (card) dispatch(setCurrentCard({ emi, cardId: card.id }));

    navigate(`/account/${accountId}`);
  };

  const renderItems = (accounts: Account[], secured?: boolean) => {
    return (
      <Space
        className='w100'
        split={<Divider type='horizontal' style={{ margin: 0 }} />}
        direction='vertical'
        size={0}
        wrap
      >
        {accounts.map((account) => (
          <AccountItem
            key={account.id}
            account={account}
            onAccountClick={onAccountClick}
            secured={secured}
          />
        ))}
      </Space>
    );
  };

  return (
    <React.Fragment>
      <PlanContainer
        setSecured={toggleSecured}
        items={Object.values(accounts)}
        renderItems={renderItems}
        active={status === AccStatus.Active}
        availBal={totalBalance}
        loading={isLoading || isLoadingEmiMeta}
        title={planName}
        secured={secured}
      />
      <VerificationStatusModal
        status={statusModalOptions.status}
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        onAction={statusModalOptions.onAction}
        actionText={statusModalOptions.actionText}
      />
      <Modal footer={null} open={showVerificationModal} onCancel={closeVerificationModal} centered>
        <OnfidoVerification
          onComplete={() => setStatusModalOpen(true)}
          emi={emi}
          closeVerificationModal={closeVerificationModal}
        />
      </Modal>
    </React.Fragment>
  );
};

export default Plan;
